import { apiSlice } from "../../../features/api/apiSlice";
export type Diet = {
  id: string;
  title: string;
  start_date?: string;
  end_date?: string;
  status?: string;
};

export type Logbook = {
  id: string;
  user_id: number;
  logbook_diet_id: string;
  status: string;
  stage?: number;
  total_score?: string | null;
  remarks?: string | null;
  supervisor_id?: string | null;
  assessor_id?: string | null;
  assessed_by?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type Member = {
  id: string;
  user_id: number;
  membership_no?: string;
  surname?: string;
  firstname?: string;
  othernames?: string | null;
  email?: string;
  phone?: string;
  phone2?: string | null;
  state?: string;
  address?: string;
};

// export type Assessor = {
//   id: string;
//   member_id: string;
//   active: number; // 1/0
//   max_workload: number;
//   created_at?: string;
//   updated_at?: string;

//   member?: Member;
//   diets?: Diet[];
//   assigned_logbooks?: Logbook[];
//   assessed_logbooks?: Logbook[];
// };
export type Assessor = any;

export type MemberSearchResult = {
  id: string;
  user_id: number;
  membership_no?: string;
  surname?: string;
  firstname?: string;
  othernames?: string | null;
  email?: string;
  phone?: string;
};

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}
export const assessorsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAssessors: builder.query<Assessor[], void>({
      query: () => ({ url: "/assessors", method: "GET" }),
      providesTags: (res) =>
        res
          ? [
              { type: "Assessors" as const, id: "LIST" },
              ...res.map((a) => ({ type: "Assessors" as const, id: a.id })),
            ]
          : [{ type: "Assessors" as const, id: "LIST" }],
    }),

    getAssessorDetails: builder.query<Assessor, string>({
      query: (id) => ({ url: `/assessors/${id}`, method: "GET" }),
      providesTags: (r, e, id) => [{ type: "Assessors" as const, id }],
    }),

    // ✅ create: send max_workload (matches your API response)
    createAssessor: builder.mutation<
      any,
      { member_id: string; specialization?: string; max_workload?: number }
    >({
      query: (body) => ({
        url: "/assessors",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Assessors" as const, id: "LIST" }],
    }),

    // You can still use these if you want "fresh" list instead of relying on index embedded arrays
    getAssessorDiets: builder.query<any, string>({
      query: (assessorId) => ({
        url: `/assessors/${assessorId}/diets`,
        method: "GET",
      }),
    }),

    getAssessorLogbooks: builder.query<any, string>({
      query: (assessorId) => ({
        url: `/assessors/${assessorId}/logbooks`,
        method: "GET",
      }),
    }),

    // searchMembers: builder.query<any[], string>({
    //   query: (q) => `/members/search/${encodeURIComponent(q)}`,
    //   transformResponse: (res: any) => pick(res) || res || [],
    //   keepUnusedDataFor: 20,
    // }),
    // Route is GET /members/search/{query?} (path param). NOTE: the endpoint name
    // must be unique — another module injects a `searchMembers` endpoint into the
    // same apiSlice, and RTK keeps the first-registered one, so a shared name here
    // silently resolved to that other endpoint (which expects {q}) and produced
    // `/members/search?q=undefined`. Hence `searchAssessorMembers`.
    searchAssessorMembers: builder.query<any[], string>({
      query: (q) => `/members/search/${encodeURIComponent(q)}`,
      transformResponse: (res: any) => res?.data || res || [],
      keepUnusedDataFor: 20,
    }),

    // ✅ update active + max_workload
    updateAssessor: builder.mutation<
      any,
      { id: string; active?: boolean; max_workload?: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/assessors/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (r, e, arg) => [
        { type: "Assessors" as const, id: arg.id },
      ],
    }),

    // ✅ remove an assessor entirely (also revokes their assessor role)
    deleteAssessor: builder.mutation<any, string>({
      query: (id) => ({
        url: `/assessors/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Assessors" as const, id: "LIST" }],
    }),

    // ✅ assign diets
    assignDietsToAssessor: builder.mutation<
      any,
      { assessorId: string; diet_ids: string[] }
    >({
      query: ({ assessorId, diet_ids }) => ({
        url: `/assessors/${assessorId}/assign-diets`,
        method: "POST",
        body: { diet_ids },
      }),
      invalidatesTags: (r, e, arg) => [
        { type: "Assessors" as const, id: arg.assessorId },
      ],
    }),

    // ✅ detach diet
    removeDietFromAssessor: builder.mutation<
      any,
      { assessorId: string; dietId: string }
    >({
      query: ({ assessorId, dietId }) => ({
        url: `/assessors/${assessorId}/diets/${dietId}`,
        method: "DELETE",
      }),
      invalidatesTags: (r, e, arg) => [
        { type: "Assessors" as const, id: arg.assessorId },
      ],
    }),

    // ✅ logbook: remove assessor
    removeAssessorFromLogbook: builder.mutation<any, { logbookId: string }>({
      query: ({ logbookId }) => ({
        url: `/logbook/${logbookId}/remove-assessor`,
        method: "POST",
      }),
    }),

    // ✅ logbook: reassign (manual)
    reassignAssessorForLogbook: builder.mutation<
      any,
      { logbookId: string; assessor_id: string }
    >({
      query: ({ logbookId, assessor_id }) => ({
        url: `/logbook/${logbookId}/reassign`,
        method: "POST",
        body: { assessor_id },
      }),
    }),

    // ✅ logbook: auto reassign
    autoReassignAssessorForLogbook: builder.mutation<
      any,
      { logbookId: string }
    >({
      query: ({ logbookId }) => ({
        url: `/logbook/${logbookId}/auto-reassign`,
        method: "POST",
      }),
    }),

    // ✅ logbook: assess/finalize
    assessLogbook: builder.mutation<any, { logbookId: string; payload: any }>({
      query: ({ logbookId, payload }) => ({
        url: `/logbook/${logbookId}/assess`,
        method: "POST",
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetAssessorDetailsQuery,
  useCreateAssessorMutation,
  useAssignDietsToAssessorMutation,
  useGetAssessorDietsQuery,
  useGetAssessorLogbooksQuery,
  useSearchAssessorMembersQuery,

  useGetAssessorsQuery,
  useUpdateAssessorMutation,
  useDeleteAssessorMutation,
  useRemoveDietFromAssessorMutation,
  useRemoveAssessorFromLogbookMutation,
  useReassignAssessorForLogbookMutation,
  useAutoReassignAssessorForLogbookMutation,
  useAssessLogbookMutation,
} = assessorsApi;
